import { Injectable } from '@angular/core';
import { ConnectionOrchestrator } from './connection/connection-manager.service';
import { IndexedDBLogStorage } from './logging/indexed-db-log-storage';

/**
 * Filter options for console log copying
 */
export interface LogFilter {
  /** Only logs from the last N seconds */
  timeRangeSeconds?: number;
  /** Only logs of these levels (LOG, ERROR, WARN, INFO) */
  levels?: ('LOG' | 'ERROR' | 'WARN' | 'INFO')[];
  /** Only logs containing ALL of these terms (case-insensitive) */
  searchTerms?: string[];
  /** Exclude logs containing ANY of these terms (case-insensitive) */
  excludeTerms?: string[];
  /** Limit to N most recent lines */
  maxLines?: number;
}

/**
 * Log entry for batch operations
 */
interface LogEntry {
  content: string;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class ConsoleLogsCaptureService {
  private consoleLogs: string[] = [];
  private originalConsoleLog: any;
  private originalConsoleError: any;
  private originalConsoleWarn: any;
  private originalConsoleInfo: any;
  private isCapturing = false;
  private isPaused = false;

  // Auto-purge configuration
  private readonly MAX_LOGS = 10000;  // Increased capacity with IndexedDB
  private readonly MAX_AGE_HOURS = 2;  // Auto-purge logs older than 2 hours
  private readonly STORAGE_KEY = 'finderwise_console_logs';
  private readonly RECORDING_ENABLED_KEY = 'finderwise_console_logs_recording_enabled';
  private readonly TRACE_UI_ENABLED_KEY = 'finderwise_trace_ui_enabled';

  // Batch write configuration for performance
  private saveTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly SAVE_DEBOUNCE_MS = 2000;  // Debounce saves to every 2 seconds

  // IndexedDB storage (async, non-blocking)
  private indexedDBStorage: IndexedDBLogStorage | null = null;
  private pendingLogs: LogEntry[] = [];  // Buffer for batch writes to IndexedDB
  private useIndexedDB = false;

  constructor(private connectionManager: ConnectionOrchestrator) {
    // Initialize IndexedDB if supported
    if (IndexedDBLogStorage.isSupported()) {
      this.indexedDBStorage = new IndexedDBLogStorage();
      this.useIndexedDB = true;
    }

    // Recording is ON by default (opt-out model)
    if (this.isRecordingEnabled()) {
      this.loadLogsFromStorage();
      this.startCapturing(); // Auto-start capturing
    }

    // Flush pending saves before page unload to prevent data loss
    window.addEventListener('beforeunload', () => this.flushPendingSave());
  }

  /**
   * Check if recording is enabled
   * Default: ON (opt-out model for better debugging)
   */
  isRecordingEnabled(): boolean {
    const enabled = localStorage.getItem(this.RECORDING_ENABLED_KEY);
    // Default to true if not set (opt-out model)
    return enabled !== 'false';
  }

  /**
   * Enable recording (opt-in)
   */
  enableRecording(): void {
    localStorage.setItem(this.RECORDING_ENABLED_KEY, 'true');
    this.startCapturing();
  }

  /**
   * Disable recording (opt-out)
   */
  disableRecording(): void {
    localStorage.setItem(this.RECORDING_ENABLED_KEY, 'false');
    this.stopCapturing();
    this.clearLogs();
  }

  /**
   * Check if trace UI is enabled
   */
  isTraceUIEnabled(): boolean {
    const enabled = localStorage.getItem(this.TRACE_UI_ENABLED_KEY);
    // Default to false (trace UI hidden by default for better UX)
    return enabled === 'true';
  }

  /**
   * Enable trace UI visibility
   */
  enableTraceUI(): void {
    localStorage.setItem(this.TRACE_UI_ENABLED_KEY, 'true');
  }

  /**
   * Disable trace UI visibility
   */
  disableTraceUI(): void {
    localStorage.setItem(this.TRACE_UI_ENABLED_KEY, 'false');
  }

  /**
   * Start capturing console logs
   */
  startCapturing(): void {
    if (this.isCapturing) return;

    // Store original console methods
    this.originalConsoleLog = console.log;
    this.originalConsoleError = console.error;
    this.originalConsoleWarn = console.warn;
    this.originalConsoleInfo = console.info;

    // Override console methods
    console.log = (...args: any[]) => {
      const timestamp = new Date().toISOString();
      const logEntry = `[${timestamp}] [LOG] ${args.map(arg =>
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ')}`;

      this.addLog(logEntry);

      // Call original method
      this.originalConsoleLog.apply(console, args);
    };

    console.error = (...args: any[]) => {
      const timestamp = new Date().toISOString();
      const logEntry = `[${timestamp}] [ERROR] ${args.map(arg =>
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ')}`;

      this.addLog(logEntry);

      this.originalConsoleError.apply(console, args);
    };

    console.warn = (...args: any[]) => {
      const timestamp = new Date().toISOString();
      const logEntry = `[${timestamp}] [WARN] ${args.map(arg =>
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ')}`;

      this.addLog(logEntry);

      this.originalConsoleWarn.apply(console, args);
    };

    console.info = (...args: any[]) => {
      const timestamp = new Date().toISOString();
      const logEntry = `[${timestamp}] [INFO] ${args.map(arg =>
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ')}`;

      this.addLog(logEntry);

      this.originalConsoleInfo.apply(console, args);
    };

    this.isCapturing = true;
  }

  /**
   * Stop capturing console logs and restore original console methods
   */
  stopCapturing(): void {
    if (!this.isCapturing) return;

    // Flush any pending saves before stopping
    this.flushPendingSave();

    if (this.originalConsoleLog) {
      console.log = this.originalConsoleLog;
    }
    if (this.originalConsoleError) {
      console.error = this.originalConsoleError;
    }
    if (this.originalConsoleWarn) {
      console.warn = this.originalConsoleWarn;
    }
    if (this.originalConsoleInfo) {
      console.info = this.originalConsoleInfo;
    }

    this.isCapturing = false;
  }

  /**
   * Copy console logs to clipboard with optional filtering
   * @param filter Optional filter criteria to limit which logs are copied
   */
  copyConsoleLogsToClipboard(filter?: LogFilter): void {
    // Get pre-Angular logs from window.__preAngularLogs (captured in index.html)
    const preAngularLogs = (window as any).__preAngularLogs || [];
    const preAngularCount = preAngularLogs.length;

    // Combine pre-Angular logs with Angular logs
    const allCapturedLogs = [...preAngularLogs, ...this.consoleLogs];

    // Apply filtering
    const filteredLogs = this.applyFilter(allCapturedLogs, filter);
    const filteredCount = filteredLogs.length;
    const totalCount = allCapturedLogs.length;

    // Add header with useful debugging info
    const header = [
      '=== FINDERWISE CONSOLE LOGS ===',
      `Captured at: ${new Date().toISOString()}`,
      `Total logs captured: ${totalCount} (${preAngularCount} pre-Angular + ${this.consoleLogs.length} Angular)`,
      `Filtered logs: ${filteredCount}`,
      filter ? `Filter applied: ${JSON.stringify(filter, null, 2)}` : 'No filter applied',
      `User Agent: ${navigator.userAgent}`,
      `URL: ${window.location.href}`,
      `Client ID: ${this.connectionManager.getUserIdentifier()}`,
      `WebSocket Connected: ${this.connectionManager.isWebSocketAvailable()}`,
      '==============================',
      '',
      '--- PRE-ANGULAR LOGS (captured from page load, even if Angular never started) ---',
      ''
    ].join('\n');

    // Combine all logs
    const allLogs = header + filteredLogs.join('\n');

    // Check if clipboard API is available
    if (navigator.clipboard && navigator.clipboard.writeText) {
      // Modern clipboard API
      navigator.clipboard.writeText(allLogs).then(() => {
        // Show success message using original console.log
        this.originalConsoleLog(`[CONSOLE-CAPTURE] ${filteredCount} console logs copied to clipboard! (${totalCount} total captured)`);

        // Show visual feedback - find the most recently clicked log button
        this.updateButtonFeedback(filteredCount);
      }).catch(err => {
        this.originalConsoleError('[CONSOLE-CAPTURE] Failed to copy logs to clipboard:', err);
        this.copyUsingFallback(allLogs, filteredCount);
      });
    } else {
      // No clipboard API available, use fallback immediately
      this.originalConsoleLog('[CONSOLE-CAPTURE] Clipboard API not available, using fallback method');
      this.copyUsingFallback(allLogs, filteredCount);
    }
  }

  private copyUsingFallback(text: string, filteredCount?: number): void {
    const logCount = filteredCount || this.consoleLogs.length;

    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '0';
      textarea.style.width = '2em';
      textarea.style.height = '2em';
      textarea.style.padding = '0';
      textarea.style.border = 'none';
      textarea.style.outline = 'none';
      textarea.style.boxShadow = 'none';
      textarea.style.background = 'transparent';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      const success = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (success) {
        this.originalConsoleLog(`[CONSOLE-CAPTURE] ${logCount} console logs copied using fallback method!`);

        // Show visual feedback
        this.updateButtonFeedback(logCount);
      } else {
        this.originalConsoleError('[CONSOLE-CAPTURE] document.execCommand copy failed');
        // Last resort: show logs in console for manual copy
        this.originalConsoleLog('=== CONSOLE LOGS FOR MANUAL COPY ===');
        this.originalConsoleLog(text);
        this.originalConsoleLog('=== END OF LOGS ===');
        alert('Unable to copy logs to clipboard. Check browser console for manual copy.');
      }
    } catch (err) {
      this.originalConsoleError('[CONSOLE-CAPTURE] Fallback copy method failed:', err);
      alert('Copy to clipboard failed. Check browser console for logs.');
    }
  }

  /**
   * Get the number of captured logs
   */
  getLogCount(): number {
    return this.consoleLogs.length;
  }

  /**
   * Clear all captured logs
   */
  clearLogs(): void {
    // Pause capturing to prevent logs generated during clear from being captured
    this.isPaused = true;

    // Cancel any pending save - we're clearing everything
    this.cancelPendingSave();
    this.pendingLogs = [];  // Clear pending IndexedDB buffer

    this.consoleLogs = [];
    localStorage.removeItem(this.STORAGE_KEY);

    // Clear IndexedDB if available
    if (this.useIndexedDB && this.indexedDBStorage) {
      this.indexedDBStorage.clearLogs().catch(err => {
        if (this.originalConsoleError) {
          this.originalConsoleError('[CONSOLE-CAPTURE] Failed to clear IndexedDB:', err);
        }
      });
    }

    this.originalConsoleLog('[CONSOLE-CAPTURE] All console logs cleared!');

    // Resume capturing after a short delay to allow change detection to complete
    setTimeout(() => {
      this.isPaused = false;
      this.originalConsoleLog('[CONSOLE-CAPTURE] Log capturing resumed');
    }, 200);
  }

  /**
   * Update button visual feedback to show copy count
   */
  private updateButtonFeedback(count: number): void {
    // Find the most likely button that was clicked
    // Look for buttons containing log-related keywords
    const buttons = Array.from(document.querySelectorAll('button'));
    const logButton = buttons.find(btn => {
      const text = btn.textContent || '';
      return (
        text.includes('All Logs') ||
        text.includes('WebSocket Logs') ||
        text.includes('Recent Errors') ||
        text.includes('Last 60 Seconds') ||
        text.includes('Logs')
      );
    });

    if (logButton) {
      const originalText = logButton.textContent;
      logButton.textContent = `✓ Copied ${count} logs!`;
      setTimeout(() => {
        logButton.textContent = originalText;
      }, 2000);
    }
  }

  /**
   * Apply filter to logs and return filtered array
   */
  private applyFilter(logs: string[], filter?: LogFilter): string[] {
    if (!filter) {
      return logs;
    }

    let filteredLogs = [...logs];

    // Apply time range filter
    if (filter.timeRangeSeconds) {
      const cutoffTime = new Date(Date.now() - filter.timeRangeSeconds * 1000).toISOString();
      filteredLogs = filteredLogs.filter(log => {
        const timestampMatch = log.match(/\[(.*?)\]/);
        if (!timestampMatch) return false;
        const timestamp = timestampMatch[1];
        return timestamp >= cutoffTime;
      });
    }

    // Apply level filter
    if (filter.levels && filter.levels.length > 0) {
      const levelPattern = filter.levels.join('|');
      const regex = new RegExp(`\\[(${levelPattern})\\]`);
      filteredLogs = filteredLogs.filter(log => regex.test(log));
    }

    // Apply search terms (AND logic - all terms must be present)
    if (filter.searchTerms && filter.searchTerms.length > 0) {
      filteredLogs = filteredLogs.filter(log =>
        filter.searchTerms!.every(term =>
          log.toLowerCase().includes(term.toLowerCase())
        )
      );
    }

    // Apply exclude terms
    if (filter.excludeTerms && filter.excludeTerms.length > 0) {
      filteredLogs = filteredLogs.filter(log =>
        !filter.excludeTerms!.some(term =>
          log.toLowerCase().includes(term.toLowerCase())
        )
      );
    }

    // Apply max lines limit (most recent)
    if (filter.maxLines && filteredLogs.length > filter.maxLines) {
      filteredLogs = filteredLogs.slice(-filter.maxLines);
    }

    return filteredLogs;
  }

  /**
   * Add a log entry with automatic size management
   */
  private addLog(logEntry: string): void {
    // Skip if capturing is paused (e.g., during clearLogs)
    if (this.isPaused) {
      return;
    }

    const timestamp = Date.now();
    this.consoleLogs.push(logEntry);

    // Buffer for batch write to IndexedDB
    if (this.useIndexedDB) {
      this.pendingLogs.push({ content: logEntry, timestamp });
    }

    // Trim in-memory logs if exceeds max
    if (this.consoleLogs.length > this.MAX_LOGS) {
      this.consoleLogs = this.consoleLogs.slice(-this.MAX_LOGS);
    }

    // Periodically purge old logs (every 500 new logs for better performance)
    if (this.consoleLogs.length % 500 === 0) {
      this.purgeOldLogs();
    }

    // Debounced persist to storage (batch writes for performance)
    this.scheduleSave();
  }

  /**
   * Schedule a debounced save to storage
   * This prevents blocking the main thread on every log entry
   */
  private scheduleSave(): void {
    if (this.saveTimeout) {
      return; // Already scheduled
    }

    this.saveTimeout = setTimeout(() => {
      this.saveLogsToStorage();
      this.saveTimeout = null;
    }, this.SAVE_DEBOUNCE_MS);
  }

  /**
   * Flush any pending save immediately
   * Used before page unload or when stopping capture
   */
  private flushPendingSave(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
      this.saveLogsToStorage();
    }
  }

  /**
   * Cancel any pending save without writing
   * Used when clearing logs
   */
  private cancelPendingSave(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
  }

  /**
   * Remove logs older than MAX_AGE_HOURS
   */
  private purgeOldLogs(): void {
    const cutoffTime = new Date();
    cutoffTime.setHours(cutoffTime.getHours() - this.MAX_AGE_HOURS);
    const cutoffISO = cutoffTime.toISOString();

    const initialCount = this.consoleLogs.length;
    this.consoleLogs = this.consoleLogs.filter(log => {
      const timestampMatch = log.match(/\[(.*?)\]/);
      if (!timestampMatch) return true; // Keep logs without timestamps
      const timestamp = timestampMatch[1];
      return timestamp >= cutoffISO;
    });

    const purgedCount = initialCount - this.consoleLogs.length;
    if (purgedCount > 0 && this.originalConsoleLog) {
      this.originalConsoleLog(`[CONSOLE-CAPTURE] Auto-purged ${purgedCount} old logs (older than ${this.MAX_AGE_HOURS} hours)`);
    }
  }

  /**
   * Get summary statistics about captured logs
   */
  getLogSummary(): {
    total: number;
    errors: number;
    warnings: number;
    recent: number;
    oldestTimestamp: string;
    newestTimestamp: string;
  } {
    const now = new Date();
    const oneMinuteAgo = new Date(now.getTime() - 60000);

    return {
      total: this.consoleLogs.length,
      errors: this.consoleLogs.filter(log => log.includes('[ERROR]')).length,
      warnings: this.consoleLogs.filter(log => log.includes('[WARN]')).length,
      recent: this.consoleLogs.filter(log => {
        const timestampMatch = log.match(/\[(.*?)\]/);
        if (!timestampMatch) return false;
        return new Date(timestampMatch[1]) >= oneMinuteAgo;
      }).length,
      oldestTimestamp: this.extractTimestamp(this.consoleLogs[0]) || 'N/A',
      newestTimestamp: this.extractTimestamp(this.consoleLogs[this.consoleLogs.length - 1]) || 'N/A'
    };
  }

  /**
   * Extract timestamp from log entry
   */
  private extractTimestamp(logEntry: string): string | null {
    if (!logEntry) return null;
    const match = logEntry.match(/\[(.*?)\]/);
    return match ? match[1] : null;
  }

  /**
   * Save logs to storage (IndexedDB preferred, localStorage fallback)
   */
  private saveLogsToStorage(): void {
    // Only save if recording is enabled
    if (!this.isRecordingEnabled()) {
      return;
    }

    // Use IndexedDB if available (async, non-blocking)
    if (this.useIndexedDB && this.indexedDBStorage && this.pendingLogs.length > 0) {
      const logsToSave = [...this.pendingLogs];
      this.pendingLogs = [];  // Clear buffer immediately

      // Fire and forget - don't block main thread
      this.indexedDBStorage.addLogs(logsToSave).then(() => {
        // Periodically trim old logs in IndexedDB
        return this.indexedDBStorage!.trimToMaxLogs();
      }).catch(err => {
        // On IndexedDB error, fall back to localStorage
        if (this.originalConsoleError) {
          this.originalConsoleError('[CONSOLE-CAPTURE] IndexedDB save failed, using localStorage fallback:', err);
        }
        this.saveToLocalStorage();
      });
      return;
    }

    // Fallback to localStorage
    this.saveToLocalStorage();
  }

  /**
   * Save to localStorage (sync fallback)
   */
  private saveToLocalStorage(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.consoleLogs));
    } catch (err) {
      // If storage is full, remove oldest half and try again
      if (err instanceof Error && err.name === 'QuotaExceededError') {
        this.consoleLogs = this.consoleLogs.slice(Math.floor(this.consoleLogs.length / 2));
        try {
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.consoleLogs));
        } catch {
          // Give up if still fails
        }
      }
    }
  }

  /**
   * Load logs from storage on initialization
   * Tries IndexedDB first, falls back to localStorage
   */
  private loadLogsFromStorage(): void {
    // First, load from localStorage for immediate availability
    this.loadFromLocalStorage();

    // Then async load from IndexedDB (may have more logs)
    if (this.useIndexedDB && this.indexedDBStorage) {
      this.indexedDBStorage.getLogs().then(logs => {
        if (logs.length > 0) {
          // Merge IndexedDB logs with any already captured
          const existingSet = new Set(this.consoleLogs);
          for (const log of logs) {
            if (!existingSet.has(log)) {
              this.consoleLogs.push(log);
            }
          }
          // Sort by timestamp
          this.consoleLogs.sort((a, b) => {
            const timestampA = this.extractTimestamp(a) || '';
            const timestampB = this.extractTimestamp(b) || '';
            return timestampA.localeCompare(timestampB);
          });
          // Trim to max
          if (this.consoleLogs.length > this.MAX_LOGS) {
            this.consoleLogs = this.consoleLogs.slice(-this.MAX_LOGS);
          }
        }

        // Purge old logs in IndexedDB
        return this.indexedDBStorage!.purgeOldLogs(this.MAX_AGE_HOURS);
      }).catch(err => {
        if (this.originalConsoleError) {
          this.originalConsoleError('[CONSOLE-CAPTURE] Failed to load from IndexedDB:', err);
        }
      });
    }
  }

  /**
   * Load from localStorage (sync fallback)
   */
  private loadFromLocalStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.consoleLogs = JSON.parse(stored);
        this.purgeOldLogs(); // Clean up old logs immediately
      }
    } catch (err) {
      // If corrupted, start fresh
      this.consoleLogs = [];
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }
}